import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { portalService } from '@/services/portalService';
import { setTokens } from '@/services/tokenStore';
import type { AcceptInvitationRequest } from '@/types/portal';
import styles from './InvitationPage.module.css';

/**
 * Validates a CPF string using the check digit algorithm.
 */
function isValidCpf(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits.charAt(10))) return false;

  return true;
}

/**
 * Applies a CPF mask to a string of digits.
 */
function applyCpfMask(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Applies a phone mask.
 */
function applyPhoneMask(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Formats an ISO date string to pt-BR locale.
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Public invitation acceptance page.
 *
 * Accessed via /portal/invite/:token.
 * Fetches invitation details by token.
 * Shows pre-filled patient data for expired/cancelled invitations
 * with appropriate error messages. For valid invitations, displays
 * an acceptance form to confirm/update personal data.
 *
 * On accept: creates patient record, redirects to portal dashboard.
 * Mobile-first design with clinic branding.
 */
export function InvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [formName, setFormName] = useState('');
  const [formCpf, setFormCpf] = useState('');
  const [formBirthDate, setFormBirthDate] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isAccepted, setIsAccepted] = useState(false);

  const {
    data: invitation,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => portalService.getInvitationByToken(token ?? ''),
    enabled: Boolean(token),
  });

  // Pre-fill form when invitation data loads
  const hasPreFilled = useState(false);
  if (invitation && !hasPreFilled[0]) {
    setFormName(invitation.patientName);
    if (invitation.patientCpf) setFormCpf(applyCpfMask(invitation.patientCpf));
    if (invitation.patientEmail) setFormEmail(invitation.patientEmail);
    if (invitation.patientPhone) setFormPhone(applyPhoneMask(invitation.patientPhone));
    hasPreFilled[1](true);
  }

  const acceptMutation = useMutation({
    mutationFn: (data: AcceptInvitationRequest) =>
      portalService.acceptInvitation(token ?? '', data),
    onSuccess: (authResponse) => {
      setTokens(authResponse.accessToken, authResponse.refreshToken);
      setIsAccepted(true);
    },
    onError: () => {
      setFormError('Erro ao aceitar convite. Tente novamente.');
    },
  });

  const handleCpfChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormCpf(applyCpfMask(e.target.value));
    },
    [],
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormPhone(applyPhoneMask(e.target.value));
    },
    [],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setFormError(null);

      if (!formName.trim()) {
        setFormError('Nome completo é obrigatório.');
        return;
      }

      const cpfDigits = formCpf.replace(/\D/g, '');
      if (!cpfDigits) {
        setFormError('CPF é obrigatório.');
        return;
      }
      if (!isValidCpf(cpfDigits)) {
        setFormError('CPF inválido. Verifique os dígitos informados.');
        return;
      }

      if (!formBirthDate) {
        setFormError('Data de nascimento é obrigatória.');
        return;
      }

      if (!formEmail.trim()) {
        setFormError('E-mail é obrigatório para criar sua conta.');
        return;
      }

      if (formPassword.length < 8) {
        setFormError('A senha deve ter no mínimo 8 caracteres.');
        return;
      }

      const payload: AcceptInvitationRequest = {
        name: formName.trim(),
        cpf: cpfDigits,
        password: formPassword,
        birthDate: formBirthDate,
        email: formEmail.trim(),
        phone: formPhone.replace(/\D/g, '') || undefined,
      };

      acceptMutation.mutate(payload);
    },
    [formName, formCpf, formBirthDate, formEmail, formPhone, formPassword, acceptMutation],
  );

  const handleGoToDashboard = useCallback(() => {
    navigate('/portal/dashboard');
  }, [navigate]);

  const handleGoToSchedule = useCallback(() => {
    navigate('/portal/schedule');
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.loadingState} role="status" aria-busy="true">
            <Clock size={32} className={styles.loadingIcon} aria-hidden="true" />
            <p>Carregando convite...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error fetching invitation
  if (queryError || !invitation) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorState} role="alert">
            <AlertCircle size={48} className={styles.errorIcon} aria-hidden="true" />
            <h1 className={styles.errorTitle}>Convite não encontrado</h1>
            <p className={styles.errorMessage}>
              O link do convite é inválido ou não existe. Verifique o link recebido
              ou entre em contato com a clínica.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Expired invitation
  if (invitation.status === 'EXPIRED') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorState} role="alert">
            <Clock size={48} className={styles.expiredIcon} aria-hidden="true" />
            <h1 className={styles.errorTitle}>Convite expirado</h1>
            <p className={styles.errorMessage}>
              Este convite expirou em {formatDate(invitation.expiresAt)}.
              Entre em contato com a clínica para receber um novo convite.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Cancelled invitation
  if (invitation.status === 'CANCELLED') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorState} role="alert">
            <XCircle size={48} className={styles.errorIcon} aria-hidden="true" />
            <h1 className={styles.errorTitle}>Convite cancelado</h1>
            <p className={styles.errorMessage}>
              Este convite foi cancelado. Entre em contato com a clínica para
              mais informações.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Already accepted
  if (invitation.status === 'ACCEPTED') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successState} role="status">
            <CheckCircle size={48} className={styles.successIcon} aria-hidden="true" />
            <h1 className={styles.successTitle}>Convite já aceito</h1>
            <p className={styles.successMessage}>
              Este convite já foi aceito anteriormente.
            </p>
            <Button onClick={handleGoToDashboard} fullWidth>
              Ir para meu painel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state after accepting
  if (isAccepted) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successState} role="status">
            <CheckCircle size={48} className={styles.successIcon} aria-hidden="true" />
            <h1 className={styles.successTitle}>Convite aceito!</h1>
            <p className={styles.successMessage}>
              Seu cadastro foi realizado com sucesso. Agora você pode agendar
              seus procedimentos.
            </p>
            <div className={styles.successActions}>
              <Button onClick={handleGoToSchedule} fullWidth>
                Agendar procedimentos
              </Button>
              <Button variant="outline" onClick={handleGoToDashboard} fullWidth>
                Ir para meu painel
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pending invitation - show accept form
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <header className={styles.header}>
          <div className={styles.logo}>
            <span className={styles.logoIcon} aria-hidden="true">+</span>
            <span className={styles.logoText}>
              CRM<span className={styles.logoAccent}>Clinica</span>
            </span>
          </div>
          <h1 className={styles.title}>Você foi convidado!</h1>
          <p className={styles.subtitle}>
            Confirme seus dados para aceitar o convite e acessar a clínica.
          </p>
          <p className={styles.expiryNote}>
            Válido até {formatDate(invitation.expiresAt)}
          </p>
        </header>

        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <Input
            label="Nome completo"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Seu nome completo"
            required
            fullWidth
          />

          <div className={styles.formRow}>
            <Input
              label="CPF"
              value={formCpf}
              onChange={handleCpfChange}
              placeholder="000.000.000-00"
              inputMode="numeric"
              required
              fullWidth
            />
            <Input
              label="Data de nascimento"
              type="date"
              value={formBirthDate}
              onChange={(e) => setFormBirthDate(e.target.value)}
              required
              fullWidth
            />
          </div>

          <Input
            label="E-mail"
            type="email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder="seu@email.com"
            required
            fullWidth
          />

          <Input
            label="Senha"
            type="password"
            value={formPassword}
            onChange={(e) => setFormPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            required
            fullWidth
          />

          <Input
            label="Telefone (opcional)"
            value={formPhone}
            onChange={handlePhoneChange}
            placeholder="(00) 00000-0000"
            inputMode="tel"
            fullWidth
          />

          {formError && (
            <p className={styles.formError} role="alert">
              {formError}
            </p>
          )}

          <Button
            type="submit"
            fullWidth
            isLoading={acceptMutation.isPending}
          >
            Aceitar convite
          </Button>
        </form>
      </div>
    </div>
  );
}
